import React, { useMemo } from 'react'
import styled from 'styled-components'

import { Section } from '../../components/Section'
import { Attribute } from '../../components/Attribute'

import {
  InspectorLayersTreeNode,
  InspectorLayersTreeRegularLayerNode,
} from 'modules/shares/ArtboardDetailView/hooks/inspector'
import { LayerIcon } from './LayerIcon'
import { ComponentName, ComponentSubName } from '../../components/ComponentName'

import * as S from './Overview.styles'
import { isArtboard } from 'modules/shares/ArtboardDetailView/hooks/inspector/sketchSceneLib/inspectorTreeNodeUtilsDeprecated'

const OverlaySection = styled(Section)`
  width: 100%;
`

const OverlayAttribute = styled(Attribute)`
  align-items: center;
  display: flex;
  flex-wrap: nowrap;
  padding-right: 0;
`

type OverviewProps = {
  layerNode: InspectorLayersTreeNode
}
/**
 * Overview info of the given layerNode. The overviews shows an icon
 * corresponding to the layer type, the layer name and sometimes the symbol name path
 * or a "copy css" button when relevant.
 */
export const Overview: React.FC<OverviewProps> = ({ layerNode }) => {
  const {
    displayedName,
    symbolNamePath,
  } = useDisplayedLayerNameAndOptionalPath(layerNode)
  if (!layerNode) {
    return null
  }

  /**
   * We want to display as symbol the layers that are symbol instances
   * or the artboards that are symbol masters when the layer is an artboard.
   */
  const isSymbol =
    layerNode.type === 'symbolMaster' || layerNode.type === 'symbolInstance'

  return (
    <OverlaySection data-testid="inspector-sidebar-overview">
      <OverlayAttribute>
        <LayerIcon
          elementType={layerNode.type}
          // Layers that are exportable gets an extra icon on top of the
          // normal layer type icon.
          hasExports={layerNode.isExportable}
          isSymbol={isSymbol}
        />
        <S.OverviewComponentName>
          <ComponentName>{displayedName}</ComponentName>
          {symbolNamePath && (
            <ComponentSubName>{symbolNamePath}</ComponentSubName>
          )}
        </S.OverviewComponentName>
        {!isArtboard(layerNode) && (
          <OverviewCopyCSSButton layerNode={layerNode} />
        )}
      </OverlayAttribute>
    </OverlaySection>
  )
}

type OverviewCopyCSSButtonProps = {
  layerNode: InspectorLayersTreeRegularLayerNode
}

function OverviewCopyCSSButton({ layerNode }: OverviewCopyCSSButtonProps) {
  if (layerNode.type === 'slice') {
    return null
  }

  const cornerRadius = layerNode.corners?.radius

  const cssValuesToCopy = {
    ...layerNode.style,
    corners: cornerRadius ? { cornerRadius } : undefined,
  }

  return <S.OverviewCopyCSSButton style={cssValuesToCopy} />
}

/**
 * The name displayed in the overview is sometimes more than just the layer name.
 * Symbols can be named in Sketch with Slashes in the name.
 * E.g. `Primitive/Icon/Arrow/Right`. Slashes in symbol names act like groups,
 * and the name gets divided into symbolMasterNamePath and symbolMasterShortName
 * if that's the case.
 */
function useDisplayedLayerNameAndOptionalPath(
  layerNode: InspectorLayersTreeNode | undefined
): { displayedName: string; symbolNamePath: string | undefined } {
  return useMemo(() => {
    if (!layerNode) {
      return { displayedName: '', symbolNamePath: undefined }
    }

    if (layerNode.type !== 'symbolMaster') {
      return { displayedName: layerNode.layerName, symbolNamePath: undefined }
    }

    if (
      layerNode.symbolMasterNamePath?.length &&
      layerNode.symbolMasterShortName
    ) {
      const symbolNamePathWithDocument = `This Document/${layerNode.symbolMasterNamePath.join(
        '/'
      )}`
      return {
        displayedName: layerNode.symbolMasterShortName,
        symbolNamePath: symbolNamePathWithDocument,
      }
    }

    return {
      displayedName: layerNode.layerName,
      symbolNamePath: 'This Document',
    }
  }, [layerNode])
}
